import React from "react"
import { Tag, Image, Dropdown } from "antd"
import { EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import styles from "./CrewMembersTable.module.css"
import { formatPhoneNumber } from "utils/data-formatters"
import { routes } from "router/routes"
import PropTypes from "prop-types"

/**
 * Component for clickable crew member name cell
 */
const CrewMemberNameCell = ({ text, record }) => {
  const navigate = useNavigate()

  const handleClick = () => {
    navigate(routes.guideCrewMemberDetailsPath({ id: record.id }))
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Image src={record.image} width={40} height={40} style={{ objectFit: "cover", borderRadius: 300 }} preview={false} />
      <span onClick={handleClick} className={styles.nameLink}>
        {text}
      </span>
    </div>
  )
}

CrewMemberNameCell.propTypes = {
  text: PropTypes.string,
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    image: PropTypes.string
  }).isRequired
}

/**
 * Crew members table columns configuration
 */
export const crewMembersColumns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    align: "center",
    sorter: true,
    render: (text, record) => <CrewMemberNameCell text={text} record={record} />
  },
  {
    title: "Title",
    dataIndex: "title",
    key: "title",
    sorter: true
  },
  {
    title: "Role",
    dataIndex: "role",
    key: "role",
    sorter: true,
    render: (role) => {
      const roleName = typeof role === "object" ? role?.name : role
      return <Tag className={styles.primaryTag}>{roleName || "-"}</Tag>
    }
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    render: (phone) => formatPhoneNumber(phone)
  },
  {
    title: "Assigned to",
    dataIndex: "assignedTo",
    key: "assignedTo",
    sorter: true,
    render: (assignedTo) => {
      if (typeof assignedTo === "number") {
        return (
          <Tag className={styles.defaultTag}>
            {assignedTo} {assignedTo === 1 ? "experience" : "experiences"}
          </Tag>
        )
      }
      return <Tag className={styles.primaryTag}>{assignedTo}</Tag>
    }
  },
  {
    title: "Upcoming",
    dataIndex: "upcomingBookings",
    key: "upcomingBookings",
    sorter: true
  },
  {
    title: "Availability",
    dataIndex: "availability",
    key: "availability",
    sorter: true
  },
  {
    title: "Next Time Off",
    dataIndex: "nextTimeOff",
    key: "nextTimeOff",
    sorter: true
  },
  {
    title: "Actions",
    key: "actions",
    align: "right",
    render: () => (
      <Dropdown
        trigger={["click"]}
        menu={{
          className: styles.actionMenu,
          items: [
            {
              key: "edit",
              icon: <EditOutlined />,
              label: "Edit"
            },
            {
              key: "delete",
              icon: <DeleteOutlined className={styles.deleteIcon} />,
              label: "Delete"
            }
          ]
        }}
        placement="bottomRight"
        arrow
      >
        <span className={styles.actionTrigger}>⋯</span>
      </Dropdown>
    )
  }
]

export default crewMembersColumns
